import type { Property } from '~/core/entities/property';
import { grammarInflection } from '~~/utils';
import { replaceNbspWithSpace, floatToLocaleString } from '@/utils';
import { defaultTwitterCardMeta } from '~/constants/seo';

interface PropertyFeature {
	value: number | null;
	singular: string;
	plural: string;
}

function formatFeature({ value, singular, plural }: PropertyFeature): string {
	if (!value) return '';
	return `${value}${singular === 'm²' ? singular : ` ${grammarInflection(value, singular, plural)}`}`;
}

const generateTitle = (property: Property | null | undefined) => {
	if (!property) return '';
	const { suites, bedrooms, askingPrice, region, city, propertyType } = property;

	let benefits = '';
	if (suites && suites > 0) {
		benefits = `com ${suites} ${grammarInflection(suites, 'suíte', 'suítes')}`;
	} else if (bedrooms && bedrooms > 0) {
		benefits = `com ${bedrooms} ${grammarInflection(bedrooms, 'quarto', 'quartos')}`;
	}

	const price = floatToLocaleString(askingPrice ?? 0, {
		style: 'currency'
	});

	const sanitizedPrice = replaceNbspWithSpace(price);

	return `${propertyType.name} ${benefits} à venda em ${region}, ${city} por ${sanitizedPrice} | PilarHomes`;
};

const generateDescription = (property: Property | null | undefined) => {
	if (!property) return '';
	const {
		region,
		area,
		bedrooms,
		suites,
		parkingSpots,
		isExclusive,
		propertyType
	} = property;

	const features = [
		formatFeature({ value: area, singular: 'm²', plural: 'm²' }),
		formatFeature({ value: bedrooms, singular: 'quarto', plural: 'quartos' }),
		formatFeature({ value: suites, singular: 'suíte', plural: 'suítes' }),
		formatFeature({ value: parkingSpots, singular: 'vaga', plural: 'vagas' })
	].filter(Boolean);

	const lastPart = features.pop();
	const descriptionParts = features.length
		? `${features.join(', ')} e ${lastPart}`
		: lastPart;

	const finalPhrase = isExclusive
		? 'Descubra todos os detalhes deste imóvel exclusivo do PilarHomes!'
		: 'Descubra todos os detalhes deste imóvel no PilarHomes!';

	const propertyTypeText = propertyType
		? useCapitalize(propertyType.name)
		: 'Imóvel';

	return `Seu novo lar em ${region} te espera: ${propertyTypeText} à venda, com ${descriptionParts}. ${finalPhrase}`;
};

const generateKeyWords = (property: Property | null | undefined) => {
	if (!property) return '';
	const { region, city, area, bedrooms, suites, parkingSpots, propertyType } =
		property;

	const propertyTypeText = propertyType
		? useCapitalize(propertyType.name)
		: 'Imóvel';

	const features = [
		region,
		city,
		propertyTypeText,
		formatFeature({ value: area, singular: 'm²', plural: 'm²' }),
		formatFeature({ value: bedrooms, singular: 'quarto', plural: 'quartos' }),
		formatFeature({ value: suites, singular: 'suíte', plural: 'suítes' }),
		formatFeature({ value: parkingSpots, singular: 'vaga', plural: 'vagas' })
	].filter(Boolean);

	return `${features.join(', ')}, à venda, PilarHomes`;
};

const getSeoTagDetailsProperties = (
	property: Ref<Property | null | undefined>
) => {
	const title = () => generateTitle(property.value);
	const description = () => generateDescription(property.value);
	const keywords = () => generateKeyWords(property.value);
	const ogImage = () => property.value?.images?.[0]?.watermarkUrl ?? '';
	return {
		title,
		description,
		keywords,
		ogTitle: title,
		ogDescription: description,
		ogImage,
		twitterTitle: title,
		twitterDescription: description,
		twitterCard: defaultTwitterCardMeta,
		twitterImage: ogImage
	};
};

export const useSeoTagDetails = (
	property: Ref<Property | null | undefined>
) => {
	useSeoMeta(getSeoTagDetailsProperties(property));
};
